import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email'; // Emails GMAIL
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Par défaut
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StoreIcon from '@mui/icons-material/Store'; // Marketplaces
import WarehouseIcon from '@mui/icons-material/Warehouse';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
import { Base64 } from 'js-base64';
import JSZip from 'jszip';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AxiosConfig from '../../../../../config/AxiosConfig';
import b64toBlob from '../../../../helpers/utils/blob';
import useAttachmentURL from '../../../../hooks/messages/useAttachmentUrl';
import DownloadableAttachment from '../../../utils/common/DownloadableAttachment';
import MailIcon from '../../../utils/common/MailIcon';
import MessageStatusIcon from '../../../utils/common/MessageStatusIcon';

const TicketActivityMessage = ({
  message,
  ticket,
  tickets,
  setOpenNewTicketDialog,
  setNewTicketDialogMessage,
  mutate,
  mutateTicket,
}) => {
  const profile = useSelector((state) => state?.login?.profile);
  const theme = useTheme();
  const { getAttachmentUrl } = useAttachmentURL();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(Boolean(anchorEl));
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(
    ticket?._id || undefined
  );
  const [messageContent, setMessageContent] = useState(null);
  const [isTranslated, setIsTranslated] = useState(false);
  const [processedMessage, setProcessedMessage] = useState(null);

  const isKnownContact = Boolean(message?.contact?.reference);
  const ContactIcon = isKnownContact ? PersonIcon : PersonOutlineIcon;
  const contactColor = isKnownContact
    ? theme.palette.primary.main
    : theme.palette.warning.main;

  const downloadZipFile = async () => {
    const zip = new JSZip();

    for (const attachment of message.attachments) {
      const attachmentContent = await AxiosConfig.create(
        'get',
        `/attachments/${attachment?._id}/getContent`
      )
        .call()
        .getResponseData();

      const content = attachmentContent
        ?.split(',')[1]
        ?.replace(/_/g, '/')
        ?.replace(/-/g, '+');
      const blobContent = b64toBlob(content, attachment?.mimeType);
      zip.file(attachment.name, blobContent);
    }

    const zipData = await zip.generateAsync({
      type: 'blob',
      streamFiles: true,
    });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(zipData);
    link.download = `${ticket.reference}-attachments.zip`;
    link.click();
  };

  const handleMessageMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
  };

  const handleMessageMenuClose = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const handleDetachMessageFromTicket = (_event) => {
    handleMessageMenuClose();
    setOpenDialog(true);
  };

  const handleDetachDialogCancel = (_event) => {
    setOpenDialog(false);
  };

  const handleDetachDialogConfirm = async (_event) => {
    setOpenDialog(false);
    if (selectedTicketId === 'new') {
      setNewTicketDialogMessage(message);
      setOpenNewTicketDialog(true);
    } else if (selectedTicketId !== ticket?._id) {
      try {
        await AxiosConfig.create(
          'post',
          `/messages/${message?._id}/changeTicket`
        ).call({
          userId: profile?._id,
          ticketId: selectedTicketId,
        });

        mutate();
        mutateTicket();
      } catch (error) {
        console.error('Error changing ticket:', error);
        // You might want to add error handling UI here
      }
    }
  };

  const handleSelectChange = (event) => {
    setSelectedTicketId(event.target.value);
  };

  // Décode le message et traite les attachments inline (remplace les cid: par les vraies URLs)
  useEffect(() => {
    const processMessage = async () => {
      if (!message?.txtBody && !message?.htmlBody) {
        setProcessedMessage(null);
        return;
      }

      let decodedMessage = '';
      try {
        let contentToDecode = '';
        if (!isTranslated) {
          contentToDecode = message.htmlBody || message.txtBody || '';
        } else {
          contentToDecode =
            message.translatedHtmlBody || message.translatedTxtBody || '';
        }

        if (contentToDecode) {
          decodedMessage = Base64.decode(contentToDecode);
        }
      } catch (error) {
        console.error('Error decoding message body:', error);
        decodedMessage = 'Error decoding message content.';
      }

      if (!decodedMessage) {
        setProcessedMessage(null);
        return;
      }

      // Trouver l'index de fin (pour couper les citations)
      const indexEnd = message?.htmlBody
        ? decodedMessage.search('<br><div class="gmail_quote">')
        : decodedMessage.search(/\sLe\s\S{3}\.\s\d{2}\s/);

      // Traiter les attachments inline si nécessaire
      let processed = decodedMessage;

      // Remplacer tous les cid: par les vraies URLs ou un placeholder
      if (message.attachments?.length > 0) {
        // Récupérer toutes les URLs d'attachments en parallèle
        const attachmentUrlMap = await Promise.all(
          message.attachments
            .filter((attachment) => !attachment?.isAtt && attachment?.cid)
            .map(async (attachment) => {
              try {
                const attachmentURL = await getAttachmentUrl(attachment?._id);
                return { cid: attachment.cid, url: attachmentURL };
              } catch (error) {
                console.error(
                  `Error loading attachment ${attachment._id}:`,
                  error
                );
                return {
                  cid: attachment.cid,
                  url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E",
                };
              }
            })
        );

        // Appliquer tous les remplacements de manière séquentielle
        // Gérer les formats avec guillemets doubles et simples
        attachmentUrlMap.forEach(({ cid, url }) => {
          if (cid && url) {
            // Remplacer avec guillemets doubles
            processed = processed.replace(
              new RegExp(
                `src="cid:${cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`,
                'gi'
              ),
              `src="${url}"`
            );
            // Remplacer avec guillemets simples
            processed = processed.replace(
              new RegExp(
                `src='cid:${cid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}'`,
                'gi'
              ),
              `src="${url}"`
            );
          }
        });
      }

      // Remplacer tous les cid: restants (sans attachment correspondant) pour éviter l'erreur ERR_UNKNOWN_URL_SCHEME
      // Cela peut arriver si un email contient des références cid: qui ne correspondent à aucun attachment
      processed = processed.replace(
        /src=["']cid:([^"']+)["']/gi,
        'src="data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\'/%3E"'
      );

      // Stocker le message traité avec l'index de fin
      setProcessedMessage({ content: processed, indexEnd });
    };

    processMessage();
  }, [isTranslated, message, getAttachmentUrl]);

  useEffect(() => {
    if (processedMessage?.content) {
      const { content, indexEnd } = processedMessage;

      if (message.htmlBody) {
        setMessageContent(
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                content
                  ?.replaceAll(`html`, ``)
                  ?.replaceAll(`</p><p>`, `<br />`)
                  ?.replaceAll(`<br /><br />`, `</p><p>`)
                  ?.replaceAll(`><br />`, `></p><p>`)
                  ?.substring(0, indexEnd === -1 ? content?.length : indexEnd)
                  ?.replace(/\n/g, '')
              ),
            }}
          />
        );
      } else if (message.txtBody) {
        setMessageContent(
          <Typography component={'pre'} sx={{ whiteSpace: 'pre-line' }}>
            {DOMPurify.sanitize(
              content?.substring(
                0,
                indexEnd === -1 ? content?.length : indexEnd
              )
            )}
          </Typography>
        );
      }
    } else {
      setMessageContent(null);
    }
  }, [processedMessage, message.htmlBody, message.txtBody]);

  const getSourceIcon = (sourceCode, channelCode) => {
    if (sourceCode === 'FORM' && channelCode === 'RABBITMQ') {
      return <DescriptionIcon sx={{ color: 'cornflowerblue' }} />;
    }
    if (sourceCode === 'MAIL' && channelCode === 'MAIL') {
      return <EmailIcon sx={{ color: 'tomato' }} />;
    }
    if (sourceCode === 'MP' && channelCode === 'RABBITMQ') {
      return <StoreIcon sx={{ color: 'darkorange' }} />;
    }
    if (sourceCode === 'MP' && channelCode === 'MAIL') {
      return <StoreIcon sx={{ color: 'mediumvioletred' }} />;
    }

    return <HelpOutlineIcon sx={{ color: 'gray' }} />;
  };

  const getSourceTooltip = (sourceCode, channelCode) => {
    if (sourceCode === 'FORM' && channelCode === 'RABBITMQ') {
      return 'Message reçu via le formulaire de contact';
    }
    if (sourceCode === 'MAIL' && channelCode === 'MAIL') {
      return 'Message envoyé/reçu par e-mail (Gmail)';
    }
    if (sourceCode === 'MP' && channelCode === 'RABBITMQ') {
      return 'Message envoyé/reçu via une marketplace';
    }
    if (sourceCode === 'MP' && channelCode === 'MAIL') {
      return 'Message envoyé/reçu via email pour une marketplace';
    }

    return 'Source inconnue';
  };

  return (
    <Paper
      elevation={16}
      key={message?._id}
      sx={{
        padding: 1,
        marginBottom: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <MailIcon boxSize={32} iconSize={24} />
        <Tooltip
          title={getSourceTooltip(
            message?.source?.code,
            message?.channel?.code
          )}
        >
          {getSourceIcon(message?.source?.code, message?.channel?.code)}
        </Tooltip>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {message?.type.name !== 'Reçu' && message?.sentBy && (
            <>
              <Tooltip
                title={`${message.sentBy.firstName} ${message.sentBy.lastName}`}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'gray',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    mr: 1,
                    cursor: 'pointer',
                  }}
                >
                  {message?.sentBy
                    ? `${message.sentBy.firstName?.charAt(
                        0
                      )}${message.sentBy.lastName?.charAt(0)}`
                    : 'N/A'}
                </Box>
              </Tooltip>
            </>
          )}

          {message?.recipientType?.code === 'CONTACT' && (
            <ContactIcon sx={{ color: contactColor }} />
          )}

          {message?.recipientType?.code === 'SERVICEPROVIDER' && (
            <WarehouseIcon sx={{ color: 'icon.messages.background' }} />
          )}
          <Typography
            component="span"
            onClick={() => {
              navigator.clipboard.writeText(
                message?.type.name === 'Reçu' ? message?.from : message?.to
              );
            }}
          >
            {message?.type.name === 'Reçu' ? message?.from : message?.to}
          </Typography>
        </Box>
        <MessageStatusIcon status={message?.type} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {isTranslated ? 'Traduit' : 'Original'}
          <Switch
            onChange={() => setIsTranslated(!isTranslated)}
            defaultChecked={isTranslated}
            disabled={!message.translatedHtmlBody && !message.translatedTxtBody}
          />
          <Typography component={'span'} variant="caption">
            {new Date(message?.sentAt || message?.updatedAt).toLocaleString(
              'fr-FR',
              {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              }
            )}
          </Typography>
          <MoreVertIcon
            id="message-button"
            // aria-controls={openMenu ? "message-menu" : undefined}
            // aria-haspopup="true"
            // aria-expanded={openMenu ? "true" : undefined}
            onClick={handleMessageMenuClick}
          />
        </Box>
        <Menu
          id="message-menu"
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMessageMenuClose}
          slotProps={{
            list: {
              // "aria-labelledby": "message-button",
            },
          }}
        >
          <MenuItem
            onClick={handleDetachMessageFromTicket}
            disabled={ticket?.messages?.length <= 1}
          >
            Détacher du ticket
          </MenuItem>
        </Menu>
        <Dialog open={openDialog} onClose={handleDetachDialogCancel}>
          <DialogTitle>Rattacher ce message à un autre ticket</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Choisir s&apos;il faut rattacher ce message à un autre ticket
              existant ou à un nouveau ticket.
            </DialogContentText>
            <Select
              labelId="select-ticket-for-message-label"
              id="select-ticket-for-message"
              value={selectedTicketId}
              onChange={handleSelectChange}
              sx={{ width: '100%' }}
            >
              <MenuItem value="new">
                <em>Créer</em>
              </MenuItem>
              {tickets?.map((currentTicket) => {
                return (
                  <MenuItem key={currentTicket?._id} value={currentTicket?._id}>
                    <Tooltip
                      title={
                        currentTicket?._id === ticket?._id
                          ? 'Ticket actuel'
                          : ''
                      }
                      placement="right"
                    >
                      <Typography component={'span'}>
                        {currentTicket?.reference}
                        {currentTicket?._id === ticket?._id ? <>*</> : null}
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                );
              })}
            </Select>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetachDialogCancel}>Annuler</Button>
            <Button onClick={handleDetachDialogConfirm}>Confirmer</Button>
          </DialogActions>
        </Dialog>
        {/* <Divider />
            Action (traduire, détacher message) */}
      </Box>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography component={'span'}>
          {isTranslated ? message.translatedSubject : message.subject}
        </Typography>
        {message?.source?.code === 'MP' && message?.mpMessageURL ? (
          <>
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                window.open(message?.mpMessageURL, '_blank', 'noreferrer')
              }
              color="info"
              sx={{ margin: 1, width: '8rem' }}
              endIcon={<OpenInNewIcon />}
            >
              {message?.contact?.store?.shortName}
            </Button>
          </>
        ) : null}
      </Box>
      <Divider />
      <Box
        sx={{
          overflow: 'auto',
        }}
      >
        {messageContent}
      </Box>
      <Divider sx={{ marginBottom: 1 }} />
      {message?.attachments?.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'scroll',
            }}
          >
            {message?.attachments?.map((attachment) => {
              return (
                <DownloadableAttachment
                  key={attachment?._id}
                  attachment={attachment}
                />
              );
            })}
          </Box>
          <Tooltip
            title={message?.attachments?.map((att, index) =>
              index !== message?.attachments?.length - 1
                ? `${att.name} / `
                : `${att.name}`
            )}
          >
            <Chip
              label={`Télécharger le zip pour les (${message?.attachments?.length}) PJ`}
              onClick={downloadZipFile}
              color="primary"
              size="small"
            />
          </Tooltip>
        </Box>
      ) : null}
    </Paper>
  );
};

export default TicketActivityMessage;
